import React, { useCallback, useMemo, useState } from 'react';

const noop = () => {};

interface PanelState {
  open: boolean;
}

export interface AccordionStateProps {
  setState: (element: HTMLDivElement, options: Partial<PanelState>) => void;
  unmount: (element: HTMLDivElement) => void;
  allOpen: boolean | undefined;
  allClosed: boolean | undefined;
  openAll: () => void;
  closeAll: () => void;
  isOpen: (element: HTMLDivElement | undefined | null) => boolean | undefined;
  controlled: boolean;
  totalPanels: number;
}

const AccordionStateContext = React.createContext<AccordionStateProps>({
  setState: noop as (element: HTMLDivElement, options: Partial<PanelState>) => void,
  unmount: noop as (element: HTMLDivElement) => void,
  isOpen: () => undefined,
  allClosed: false,
  allOpen: false,
  closeAll: noop,
  openAll: noop,
  controlled: false,
  totalPanels: 0
});

export function useAccordionState() {
  return React.useContext(AccordionStateContext);
}

export const AccordionStateConsumer = AccordionStateContext.Consumer;

export const AccordionStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [panelMap, setPanelMap] = useState<Map<HTMLElement, PanelState>>(new Map());

  // Update AccordionManager state ( if Provider does not exist - setState being mocked )
  const setState = useCallback((element: HTMLDivElement, state: Partial<PanelState>) => {
    setPanelMap((prevMap) => {
      const prevState = prevMap.get(element);
      const newState = { open: false, ...prevState, ...state };
      prevMap.set(element, newState);
      return new Map(prevMap);
    });
  }, []);

  // Check if AccordionManager state is open
  const isOpen = useCallback(
    (element: HTMLDivElement | undefined | null) => {
      if (!element) {
        return false;
      }
      return panelMap.get(element)?.open ?? false;
    },
    [panelMap]
  );

  // Handle action on unmount (remove from map)
  const unmount = useCallback((element: HTMLDivElement) => {
    setPanelMap((prevMap) => {
      prevMap.delete(element);
      return new Map(prevMap);
    });
  }, []);

  const openAll = useCallback(() => {
    setPanelMap((prevMap) => {
      const panelArray = [...prevMap].map(([element, state]) => [element, { ...state, open: true }]) as [
        HTMLDivElement,
        PanelState
      ][];
      return new Map(panelArray);
    });
  }, []);

  const closeAll = useCallback(() => {
    setPanelMap((prevMap) => {
      const panelArray = [...prevMap].map(([element, state]) => [element, { ...state, open: false }]) as [
        HTMLDivElement,
        PanelState
      ][];
      return new Map(panelArray);
    });
  }, []);

  const allOpen = [...panelMap].every(([, state]) => state.open === true);

  const allClosed = [...panelMap].every(([, state]) => state.open === false);

  const totalPanels = panelMap.size;

  const value = useMemo<AccordionStateProps>(
    () => ({
      setState,
      unmount,
      isOpen,
      openAll,
      closeAll,
      allOpen,
      allClosed,
      controlled: true,
      totalPanels
    }),
    [allClosed, allOpen, closeAll, isOpen, openAll, setState, totalPanels, unmount]
  );
  return <AccordionStateContext.Provider value={value}>{children}</AccordionStateContext.Provider>;
};
